import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import Spinner from 'components/spinner/Spinner';
import SidePanel from './SidePanel';
import SidePanelHeader from './SidePanelHeader';
import SidePanelTabs from './SidePanelTabs';
import DocumentsPanel from './DocumentsPanel';
import VisualsPanel from './VisualsPanel';

import { fetchAttachments, resetAttachments, setLoadingStatus } from '../../reducers/attachments';

import { t } from 'utils/language';
import { IMAGE_ALLOWED_MIMETYPES } from 'utils/image';

import type { MessengerAttachment, Subcampaign } from 'types';

import type { SyntheticEvent } from 'react';

type GroupedAttachments = {
  visuals: Array<MessengerAttachment>,
  documents: Array<MessengerAttachment>
};

type OwnProps = {
  subcampaign: Subcampaign,
  onClosePanel: () => void,
  onCloseAllPanels: () => void
};

type StateProps = {
  conversationId: string,
  isLoading: boolean,
  attachments: Array<MessengerAttachment>
};

type DispatchProps = {
  dispatchFetchAttachments: (conversationId: string) => void,
  dispatchResetAttachments: () => void,
  dispatchSetLoadingStatus: (status: boolean) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  activeTab: string,
  visuals: Array<MessengerAttachment>,
  documents: Array<MessengerAttachment>
};

const TAB_ITEMS = ['visuals', 'documents'];

export class SidePanelAttachments extends React.PureComponent<Props, State> {
  state = {
    activeTab: TAB_ITEMS[0],
    visuals: [],
    documents: []
  };

  componentDidMount() {
    const { dispatchFetchAttachments, conversationId } = this.props;

    dispatchFetchAttachments(conversationId);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      dispatchFetchAttachments,
      dispatchResetAttachments,
      dispatchSetLoadingStatus,
      conversationId,
      attachments,
      subcampaign
    } = this.props;

    if (!isEqual(attachments, prevProps.attachments)) {
      const groupedAttachments = this.groupAttachments(attachments);
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        ...groupedAttachments
      });
    }

    if (!isEqual(conversationId, prevProps.conversationId)) {
      dispatchFetchAttachments(conversationId);
    }

    if (!isEqual(subcampaign, prevProps.subcampaign)) {
      dispatchResetAttachments();
      dispatchSetLoadingStatus(true);
    }
  }

  componentWillUnmount() {
    const { dispatchResetAttachments } = this.props;

    dispatchResetAttachments();
  }

  groupAttachments = (attachments: Array<MessengerAttachment>): GroupedAttachments => attachments.reduce((groupedAttachments: GroupedAttachments, attachment: MessengerAttachment) => {
    if (IMAGE_ALLOWED_MIMETYPES.includes(attachment.mimeType)) {
      groupedAttachments.visuals.push(attachment);
    } else {
      groupedAttachments.documents.push(attachment);
    }
    return groupedAttachments;
  }, {
    visuals: [],
    documents: []
  });

  changeActiveTab = (e: SyntheticEvent<HTMLSpanElement>) => {
    const activeTab = e.currentTarget.dataset.arg;
    if (!activeTab) {
      return;
    }
    this.setState({
      activeTab
    });
  };

  render() {
    const { onClosePanel, onCloseAllPanels, isLoading } = this.props;
    const { activeTab, visuals, documents } = this.state;

    return (
      <SidePanel>
        <section className="side-panel-attachments">
          <SidePanelHeader
            title={t('messenger.side_panel.attachments_label')}
            onClosePanel={onClosePanel}
            onCloseAllPanels={onCloseAllPanels}
          />
          <SidePanelTabs
            tabs={TAB_ITEMS}
            activeTab={activeTab}
            handleChangeActiveTab={this.changeActiveTab}
          />
          <div>
            {
              isLoading && (
                <Spinner size="small" position="inline" color="light" />
              )
            }
            {
              !isLoading && activeTab === 'visuals' && (
                <VisualsPanel data={visuals} />
              )
            }
            {
              !isLoading && activeTab === 'documents' && (
                <DocumentsPanel data={documents} />
              )
            }
          </div>
        </section>
      </SidePanel>
    );
  }
}

const mapStateToProps = (state): StateProps => ({
  conversationId: state.messages.conversationId,
  isLoading: state.attachments.isLoading,
  attachments: state.attachments.list
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchAttachments: conversationId => dispatch(fetchAttachments(conversationId)),
  dispatchResetAttachments: () => dispatch(resetAttachments()),
  dispatchSetLoadingStatus: status => dispatch(setLoadingStatus(status))
});

export default connect(mapStateToProps, mapDispatchToProps)(SidePanelAttachments);
